import React, { useState } from 'react';
import { Bike, TruckIcon, Smartphone, Calendar, DollarSign, CheckCircle, Zap } from 'lucide-react';
export const AssetMarketplace = () => {
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [hasDeposit, setHasDeposit] = useState(false);
  const assets = [{
    id: 'electric-boda',
    category: 'Two-Wheeler',
    name: 'Electric Boda Boda',
    price: 180000,
    dailyRate: 600,
    weeklyRate: 3500,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: <Zap size={32} className="text-primary-600" data-id="element-227" />,
    features: ['Electric Charging', 'Eco-Friendly', '1 Year Warranty', 'Low Running Cost']
  }, {
    id: 'electric-delivery',
    category: 'Delivery',
    name: 'Electric Delivery Bike',
    price: 220000,
    dailyRate: 750,
    weeklyRate: 4500,
    image: 'https://images.unsplash.com/photo-1609078880481-94dd78275a59?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: <TruckIcon size={32} className="text-primary-600" data-id="element-228" />,
    features: ['Cargo Capacity', 'Fast Charging', 'GPS Tracking', 'High ROI']
  }, {
    id: 'smartphone',
    category: 'Tech',
    name: 'Smartphone (Latest Models)',
    price: 45000,
    dailyRate: 150,
    weeklyRate: 900,
    image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    icon: <Smartphone size={32} className="text-primary-600" data-id="element-229" />,
    features: ['Latest Android/iOS', '128GB Storage', '1 Year Warranty', 'Free Case']
  }];
  const selectedAssetData = assets.find(a => a.id === selectedAsset);
  return <section id="asset-marketplace" className="py-20 bg-gradient-to-br from-primary-50 to-white" data-id="element-230">
      <div className="container" data-id="element-231">
        <div className="text-center mb-16" data-id="element-232">
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-5 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-233">
            <DollarSign size={18} data-id="element-234" />
            Buy Now Pay Later
          </div>
          <h2 className="section-title" data-id="element-235">Asset Inclusion Marketplace</h2>
          <p className="section-subtitle" data-id="element-236">
            Own productive assets through flexible micro-installments. Start
            earning today, pay as you go.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-237">
          {assets.map(asset => <div key={asset.id} onClick={() => setSelectedAsset(asset.id)} className={`bg-white rounded-2xl overflow-hidden border-2 transition-all duration-300 cursor-pointer card-hover ${selectedAsset === asset.id ? 'border-primary-500 shadow-2xl scale-105' : 'border-gray-200 hover:border-primary-300'}`} data-id="element-238">
              <div className="relative h-48 overflow-hidden" data-id="element-239">
                <img src={asset.image} alt={asset.name} className="w-full h-full object-cover" data-id="element-240" />
                <div className="absolute top-4 left-4 bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-bold" data-id="element-241">
                  {asset.category}
                </div>
              </div>

              <div className="p-6" data-id="element-242">
                <div className="mb-4 w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center" data-id="element-243">
                  {asset.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-244">
                  {asset.name}
                </h3>
                <p className="text-2xl font-bold text-primary-600 mb-1" data-id="element-245">
                  KSh {asset.price.toLocaleString()}
                </p>

                <div className="bg-primary-50 rounded-lg p-3 mb-4" data-id="element-246">
                  <div className="flex items-center justify-between mb-1" data-id="element-247">
                    <span className="text-sm text-gray-600" data-id="element-248">Daily</span>
                    <span className="text-lg font-bold text-primary-600" data-id="element-249">
                      KSh {asset.dailyRate}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-250">
                    <span className="text-sm text-gray-600" data-id="element-251">Weekly</span>
                    <span className="text-lg font-bold text-primary-600" data-id="element-252">
                      KSh {asset.weeklyRate}
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 mb-4" data-id="element-253">
                  {asset.features.map((feature, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-254">
                      <CheckCircle size={16} className="text-primary-600 flex-shrink-0" data-id="element-255" />
                      {feature}
                    </li>)}
                </ul>

                <button onClick={() => setSelectedAsset(asset.id)} className="w-full py-3 px-6 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-lg transition-all duration-300" data-id="element-256">
                  Get This Asset
                </button>
              </div>
            </div>)}
        </div>

        {/* Repayment Modal */}
        {selectedAsset && selectedAssetData && <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl p-8 border-2 border-primary-200 animate-fadeIn" data-id="element-257">
            <div className="flex items-center gap-4 mb-6" data-id="element-258">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center" data-id="element-259">
                {selectedAssetData.icon}
              </div>
              <div data-id="element-260">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-261">
                  {selectedAssetData.name}
                </h3>
                <p className="text-primary-600 font-semibold" data-id="element-262">Repayment Plan</p>
              </div>
            </div>

            <div className="mb-6" data-id="element-263">
              <label className="flex items-center gap-3 cursor-pointer" data-id="element-264">
                <input type="checkbox" checked={hasDeposit} onChange={e => setHasDeposit(e.target.checked)} className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500" data-id="element-265" />
                <span className="text-gray-700 font-medium" data-id="element-266">
                  I have a deposit (20% recommended)
                </span>
              </label>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-267">
              <div className="bg-primary-50 rounded-xl p-6 text-center" data-id="element-268">
                <Calendar size={32} className="text-primary-600 mx-auto mb-2" data-id="element-269" />
                <p className="text-sm text-gray-600 mb-1" data-id="element-270">Daily Payment</p>
                <p className="text-2xl font-bold text-primary-600" data-id="element-271">
                  KSh{' '}
                  {hasDeposit ? Math.round(selectedAssetData.dailyRate * 0.8) : selectedAssetData.dailyRate}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-272">For 12 months</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6 text-center" data-id="element-273">
                <Calendar size={32} className="text-primary-600 mx-auto mb-2" data-id="element-274" />
                <p className="text-sm text-gray-600 mb-1" data-id="element-275">Weekly Payment</p>
                <p className="text-2xl font-bold text-primary-600" data-id="element-276">
                  KSh{' '}
                  {hasDeposit ? Math.round(selectedAssetData.weeklyRate * 0.8) : selectedAssetData.weeklyRate}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-277">For 12 months</p>
              </div>

              <div className="bg-primary-600 text-white rounded-xl p-6 text-center" data-id="element-278">
                <DollarSign size={32} className="mx-auto mb-2" data-id="element-279" />
                <p className="text-sm mb-1" data-id="element-280">Total Cost</p>
                <p className="text-2xl font-bold" data-id="element-281">
                  KSh{' '}
                  {hasDeposit ? Math.round(selectedAssetData.price * 1.1) : Math.round(selectedAssetData.price * 1.15)}
                </p>
                <p className="text-xs opacity-90 mt-1" data-id="element-282">Including interest</p>
              </div>
            </div>

            {hasDeposit && <div className="bg-accent-50 border-2 border-accent-200 rounded-xl p-4 mb-6" data-id="element-283">
                <p className="text-sm text-accent-800" data-id="element-284">
                  <strong data-id="element-285">With 20% deposit:</strong> You'll pay KSh{' '}
                  {Math.round(selectedAssetData.price * 0.2).toLocaleString()}{' '}
                  upfront and enjoy lower installments!
                </p>
              </div>}

            <div className="flex gap-4" data-id="element-286">
              <button onClick={() => setSelectedAsset(null)} className="flex-1 py-4 px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl transition-all duration-300" data-id="element-287">
                Choose Different Asset
              </button>
              <a href="#apply-now" className="flex-1 py-4 px-6 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg text-center" data-id="element-288">
                Apply Now
              </a>
            </div>
          </div>}
      </div>
    </section>;
};